import { convertEurToUsd, getCurrencySymbol } from './genericHelpers';
import { SUBSCRIPTION_PLAN_NAMES } from './enums';

const detectTierKey = priceObj => {
  if (!priceObj) return null;

  const metaTier = priceObj?.productMetadata?.tier;
  const name = (priceObj?.productName || '').toLowerCase();

  const tierFromMeta = metaTier ? metaTier.toLowerCase() : null;

  if (tierFromMeta === 'free') return 'free';
  if (tierFromMeta === 'haul_star' || tierFromMeta === 'haulstar' || tierFromMeta === 'haul star')
    return 'haulStar';
  if (tierFromMeta === 'big_rig' || tierFromMeta === 'bigrig' || tierFromMeta === 'big rig')
    return 'bigRig';

  // Fallback: use product name if metadata not set
  if (name.includes('big rig')) return 'bigRig';
  if (name.includes('haul star')) return 'haulStar';
  if (name.includes('free')) return 'free';

  return null;
};

/**
 * Helper function to group product prices by tier.
 * @param {Array} productPrices - Array of price objects from Stripe.
 * @returns {Object} - Object with tiers as keys and corresponding price objects as values.
 */
export const getPlansByTier = (productPrices = []) => {
  return productPrices.reduce(
    (acc, price) => {
      const tierKey = detectTierKey(price);
      if (tierKey && !acc[tierKey]) {
        acc[tierKey] = price;
      }
      return acc;
    },
    { free: null, haulStar: null, bigRig: null }
  );
};

export const formatPriceLabel = priceObj => {
  const { unitAmount, currency, interval, intervalCount } = priceObj || {};

  if (!unitAmount || unitAmount === 0) {
    return 'Free';
  }

  const amount = (convertEurToUsd(unitAmount) / 100).toFixed(2);
  const cur = currency ? currency.toUpperCase() : 'USD';
  const curSymbol = getCurrencySymbol(cur);
  const count = intervalCount > 1 ? intervalCount : '';
  const intervalLabel = interval || 'month';

  return `${curSymbol} ${amount} / ${count} ${intervalLabel}`;
};

export const getSubscriptionProductData = currentUser => {
  if (!currentUser || !currentUser?.id) {
    return null;
  }

  const protectedData = currentUser?.attributes?.profile?.protectedData || {};
  const subscriptionProductId = protectedData?.subscriptionProductId;
  const subscriptionProductName = protectedData?.subscriptionProductName;

  return { subscriptionProductId, subscriptionProductName };
};

export const getSubscription = currentUser => {
  if (!currentUser || !currentUser?.id) {
    return null;
  }

  const privateData = currentUser?.attributes?.profile?.privateData || {};
  const hasSubscription =
    privateData?.subscriptionData?.id && privateData?.subscriptionData?.status === 'active';

  const subscriptionData = privateData?.subscriptionData?.id && privateData?.subscriptionData;

  return { hasSubscription, subscriptionData };
};

export const isActiveSubscriptionStatus = status => {
  const normalizedStatus = (status || '').toLowerCase();
  return normalizedStatus === 'active' || normalizedStatus === 'trialing';
};

// Haul Star "Pause instead of Cancel" (2026-10-13).
// A paused subscription keeps Stripe status 'active' but has pause_collection set;
// the server normalizes this into subscriptionData.paused + .pauseResumesAt.
// While paused: PERKS ARE OFF (badge / priority sort / contact display) but the
// member is still fundamentally a member (sub alive, auto-resumes). So:
//   isSubscriptionPaused(user) => true while paused
//   isPausedMember(user)       => true if they're a member who is currently paused
//   isHaulStarMember(user)     => PERKS-ON gate => FALSE while paused
// Use isHaulStarMember for perk gating; use isPausedMember / isSubscriptionPaused
// for membership-management UI.
export const isSubscriptionPaused = currentUser => {
  const { subscriptionData } = getSubscription(currentUser) || {};
  return !!subscriptionData?.paused;
};

export const getPauseResumesAt = currentUser => {
  const { subscriptionData } = getSubscription(currentUser) || {};
  // Stripe resumes_at is epoch SECONDS; return as-is (null when not paused).
  return subscriptionData?.pauseResumesAt || null;
};

// Product ids that count as a paid membership. The current Haul Star plus the
// grandfathered legacy paid products (old $117 "Haul Star Membership" + $67
// "Big Rig") so long-standing testers still read as members. Mirror of the
// server-side SUB_ALLOWED_PRODUCT_IDS.
export const MEMBER_PRODUCT_IDS = [
  'prod_UwGAw4tJxOrMhH', // Haul Star ($30/mo)
  'prod_TdvJ2zah4fPzxj', // legacy Haul Star Membership ($117)
  'prod_TdvLShre5TM5JH', // legacy Big Rig ($67)
];

// Internal: is this user fundamentally a member (allowed product + active/trialing)?
// TRUE even while paused. Used by both isHaulStarMember (which additionally
// requires perks-on, i.e. not paused) and the membership-management UI.
const isMemberFundamental = currentUser => {
  if (!currentUser || !currentUser?.id) {
    return false;
  }

  const { subscriptionProductId, subscriptionProductName } =
    getSubscriptionProductData(currentUser) || {};
  const { subscriptionData } = getSubscription(currentUser) || {};

  if (!isActiveSubscriptionStatus(subscriptionData?.status)) return false;

  // Primary: match by product id (robust to product-name changes).
  if (subscriptionProductId && MEMBER_PRODUCT_IDS.includes(subscriptionProductId)) {
    return true;
  }

  // Fallback: name contains "haul star" (covers "Haul Star", "Haul Star Plan",
  // "Haul Star Membership") in case product id isn't present on older records.
  const normalizedPlanName = (subscriptionProductName || '').toLowerCase();
  return normalizedPlanName.includes('haul star');
};

// PERKS-ON gate. A member whose subscription is currently PAUSED returns FALSE
// here (perks — badge, priority sort, contact display — are off during a pause)
// even though they remain a member. This is the gate the rest of the app should
// use for showing/hiding perks.
export const isHaulStarMember = currentUser => {
  if (!isMemberFundamental(currentUser)) return false;
  if (isSubscriptionPaused(currentUser)) return false;
  return true;
};

// Member who is currently paused (still a member, perks off, will auto-resume).
export const isPausedMember = currentUser =>
  isMemberFundamental(currentUser) && isSubscriptionPaused(currentUser);

export const getButtonText = (hasSubscription, stripePrice, label) => {
  let buttonText = `Upgrade to ${label}`;
  if (hasSubscription) {
    const planName = stripePrice?.productName || label;
    buttonText = `Switch to ${planName}`;
  }
  return buttonText;
};

export const getAuthorSubscriptionData = author => {
  if (!author || !author?.id) {
    return null;
  }

  const protectedData = author?.attributes?.profile?.protectedData || {};
  const subscriptionProductId = protectedData?.subscriptionProductId;
  const subscriptionProductName = protectedData?.subscriptionProductName;

  return { subscriptionProductId, subscriptionProductName };
};
